import * as React from "react"
import {Link as I18nLink} from "libs/gatsby-i18next"
import {twMerge} from "libs/twmerge"

/**
 * @typedef {object} TextLinkOwnProps
 * @property {string} href                  URL — `http(s)://`, `mailto:`, `tel:` open in a new tab; everything else uses the locale-aware Gatsby `<Link>`.
 * @property {React.ReactNode} [children]   Optional — `<Trans components={[<TextLink href=... />]} />` injects children via cloneElement.
 * @property {string} [className]
 */

const EXTERNAL = /^(https?:)?\/\/|^mailto:|^tel:/i

const isExternal = (/** @type {string} */ href) => EXTERNAL.test(href)

const baseClasses = [
  "inline cursor-pointer text-blue-primary font-bold underline underline-offset-4",
  "transition-opacity duration-200 hover:opacity-95",
  "rounded-sm outline-none",
  "focus-visible:ring-2 focus-visible:ring-blue-primary focus-visible:ring-offset-2",
  "focus-visible:ring-offset-blue-light dark:focus-visible:ring-offset-blue-deep",
].join(" ")

/**
 * Reusable hypertext link. External URLs render `<a target="_blank" rel="noreferrer noopener">`;
 * internal paths route through `gatsby-plugin-react-i18next`'s `<Link>` so locale prefix and
 * prefetching are preserved. Drop-in compatible with `<Trans components={[<TextLink href=... />]} />`.
 *
 * @type {React.ForwardRefExoticComponent<TextLinkOwnProps & React.AnchorHTMLAttributes<HTMLAnchorElement> & React.RefAttributes<HTMLAnchorElement>>}
 */
const TextLink = React.forwardRef((props, ref) => {
  const {href, children, className = "", ...rest} = props
  const composed = twMerge(baseClasses, className)

  if (isExternal(href)) {
    return (
      <a
        ref={ref}
        href={href}
        target="_blank"
        rel="noreferrer noopener"
        className={composed}
        {...rest}
      >
        {children}
      </a>
    )
  }

  return (
    <I18nLink to={href} ref={ref} className={composed} {...rest}>
      {children}
    </I18nLink>
  )
})

TextLink.displayName = "TextLink"

export default TextLink
